import { useContext } from 'react';
import { BaseWidget } from './BaseWidget';
import BrandingWatermarkOutlinedIcon from '@mui/icons-material/BrandingWatermarkOutlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import { NumberCssControl } from '../controls/NumberCssControl';
import { IconButtonGroupControl } from '../controls/IconButtonGroupControl';
import useSettings from '../../../../hooks/Settings';
import { TemplateDataContext } from '../context/TemplateDataContext';
import { ReportBrandingContext } from '../context/ReportBrandingContext';

// `useSettings().logo` defaults to the sentinel string 'none' — treat it (and
// empty) as "no logo" so we never render <img src="none">.
const cleanLogo = (value) => (value && value !== 'none' ? value : '');

/**
 * LogoWidget — renders the organisation's logo, resolved dynamically (never
 * stored on the widget) so a template instantiated by any tenant shows that
 * tenant's own logo. Resolution priority:
 *   1. template preview  → templateData.data.branding.logo (exportTemplateData)
 *   2. public viewer      → ReportBrandingContext.logo (owner logo from CF)
 *   3. editor / in-app    → useSettings().logo (logged-in tenant)
 *   4. fallback           → placeholder
 *
 * @extends {BaseWidget}
 */
export class LogoWidget extends BaseWidget {
    getName() { return 'Logo'; }
    getIcon() { return <BrandingWatermarkOutlinedIcon />; }

    getControls() {
        return {
            width: new NumberCssControl({
                label: 'Breedte',
                description: '',
                defaultValue: { value: 150, unit: 'px' },
                options: {},
            }),
            height: new NumberCssControl({
                label: 'Hoogte',
                description: '',
                defaultValue: { value: 60, unit: 'px' },
                options: {},
            }),
            align: new IconButtonGroupControl({
                label: 'Uitlijning',
                description: '',
                defaultValue: 'left',
                options: {
                    buttons: [
                        { value: 'left', icon: <FormatAlignLeftIcon />, tooltip: 'Links' },
                        { value: 'center', icon: <FormatAlignCenterIcon />, tooltip: 'Midden' },
                        { value: 'right', icon: <FormatAlignRightIcon />, tooltip: 'Rechts' },
                    ],
                },
            }),
        };
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * @param {object} props
 * @param {{ value: number, unit?: string }} [props.width]
 * @param {{ value: number, unit?: string }} [props.height]
 * @param {'left'|'center'|'right'} [props.align]
 */
const Renderer = ({ width, height, align = 'left' }) => {
    const { templateData, isTemplate } = useContext(TemplateDataContext);
    const branding = useContext(ReportBrandingContext);
    const settingsLogo = cleanLogo(useSettings().logo);

    const logo = isTemplate
        ? cleanLogo(templateData?.data?.branding?.logo)
        : (cleanLogo(branding?.logo) || settingsLogo);

    const style = {
        width: width ? `${width.value}${width.unit ?? ''}` : undefined,
        height: height ? `${height.value}${height.unit ?? ''}` : undefined,
    };

    const content = logo
        ? <img src={logo} alt="" style={style} />
        : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-image" width="48" height="48">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
        );

    // `text-align` aligns the inline logo within the full-width widget block.
    return <div style={{ textAlign: align }}>{content}</div>;
};
